import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { TextBtn } from '../../shared/ui/text-btn';
import { colors, dimensions, typography } from '../../theme';
import { VolumeRow } from './volume-row';

type VoiceoverTabProps = {
  script: string;
  onScriptChange: (next: string) => void;
  audioUrl: string | null;
  isGenerating: boolean;
  error: string | null;
  volume: number;
  onVolumeChange: (next: number) => void;
  onGenerate: () => void;
};

const PLACEHOLDER = 'Describe el guion que la IA leerá y pasará a voz';
const MAX_SCRIPT_LENGTH = 2000;
const ERROR_COLOR = '#dc2626';

/**
 * Tab Locución del SoundPanel. Componente "view" controlado:
 * el estado (script, audioUrl, isGenerating, error) vive en SoundPanel y se
 * pasa por props.
 */
export const VoiceoverTab: React.FC<VoiceoverTabProps> = ({
  script,
  onScriptChange,
  audioUrl,
  isGenerating,
  error,
  volume,
  onVolumeChange,
  onGenerate
}) => {
  const atLimit = script.length >= MAX_SCRIPT_LENGTH;
  const canGenerate = script.trim().length > 0 && !isGenerating;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: dimensions.spacing['2xl'] }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: dimensions.spacing.lg }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label
            style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              fontFamily: typography.fontFamily.ui,
              fontWeight: typography.fontWeight.medium,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wider
            }}
          >
            Texto a voz (TTS)
          </label>
          <span
            style={{
              fontSize: typography.fontSize.sm,
              color: atLimit ? ERROR_COLOR : colors.textMuted,
              fontFamily: typography.fontFamily.ui
            }}
          >
            {script.length}/{MAX_SCRIPT_LENGTH}
          </span>
        </div>
        <textarea
          value={script}
          onChange={e => onScriptChange(e.target.value)}
          placeholder={PLACEHOLDER}
          rows={4}
          maxLength={MAX_SCRIPT_LENGTH}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: dimensions.spacing.xl,
            border: `1px solid ${colors.border}`,
            borderRadius: dimensions.radius.xl,
            fontSize: typography.fontSize.md,
            fontFamily: typography.fontFamily.ui,
            color: colors.textPrimary,
            background: colors.surfaceElevated,
            resize: 'vertical',
            outline: 'none',
            minHeight: 100
          }}
        />
      </div>

      {error && (
        <p
          style={{
            color: ERROR_COLOR,
            fontSize: typography.fontSize.sm,
            fontFamily: typography.fontFamily.ui,
            margin: 0
          }}
        >
          {error}
        </p>
      )}

      {audioUrl && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: dimensions.spacing.lg }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: dimensions.spacing.lg,
              color: colors.accent,
              fontSize: typography.fontSize.md,
              fontFamily: typography.fontFamily.ui
            }}
          >
            <CheckCircle2 size={dimensions.size.iconSm} />
            <span>Audio generado correctamente</span>
          </div>
          <VolumeRow audioUrl={audioUrl} volume={volume} onVolumeChange={onVolumeChange} />
        </div>
      )}

      <TextBtn
        label='Generar audio'
        variant='primary'
        disabled={!canGenerate}
        loading={isGenerating}
        fullWidth
        onClick={onGenerate}
      />
    </div>
  );
};
